import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import CUISINES from './cuisineData';
import CITIES from './allCityData';
import './PublicPages.css';

function CuisinePage() {
  const { cuisineSlug } = useParams();
  const navigate = useNavigate();
  const cuisine = CUISINES[cuisineSlug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [cuisineSlug]);

  if (!cuisine) {
    return (
      <div className="public-page">
        <div className="content-section">
          <h1>Cuisine Not Found</h1>
          <p>Sorry, we don't have information for this cuisine yet.</p>
          <Link to="/" className="btn btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  const handleNavigate = (path) => {
    window.scrollTo(0, 0);
    navigate(path);
  };

  const bestCities = (cuisine.bestCities || []).map(slug => CITIES[slug] ? { slug, ...CITIES[slug] } : null).filter(Boolean);
  const dishList = cuisine.mustTry || [];
  const popularDishes = dishList.map(d => d.dish).join(', ');

  return (
    <div className="public-page">
      <SEO
        title={`Best ${cuisine.name} Restaurants Near You — ${cuisine.tagline || 'Swipe & Decide'}`}
        description={`Craving ${cuisine.name}? ${cuisine.description ? cuisine.description.slice(0, 100) + '...' : ''} Use Hangrily to swipe through ${cuisine.name} restaurants near you. Must-try: ${popularDishes}.`}
        path={`/cuisine/${cuisineSlug}`}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: `${cuisine.name} Restaurant Picker - Hangrily`,
            description: `Find and decide on the best ${cuisine.name} restaurants near you. ${cuisine.tagline || ''}`,
            url: `https://hangrily.app/cuisine/${cuisineSlug}`
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: `${cuisine.name} Restaurants`, item: `https://hangrily.app/cuisine/${cuisineSlug}` }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: `How do I find the best ${cuisine.name} restaurant near me?`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `Open Hangrily, set your cuisine filter to ${cuisine.name}, and allow location access. The app pulls ${cuisine.name} restaurants from Google Places near you, complete with photos and ratings. Swipe right on places that look good, left on ones that don't. Your top pick wins in under 2 minutes.`
                }
              },
              {
                '@type': 'Question',
                name: `What are must-try ${cuisine.name} dishes?`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `Essential ${cuisine.name} dishes include ${popularDishes}. ${cuisine.history ? cuisine.history.slice(0, 150) : ''}`
                }
              },
              {
                '@type': 'Question',
                name: `Can I pick a ${cuisine.name} restaurant with my group using Hangrily?`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `Yes. Start a Group Session in Hangrily with the ${cuisine.name} filter active. Up to 10 friends join via code and swipe through ${cuisine.name} restaurants independently. The app finds the ${cuisine.name} spot with the most group agreement — no arguing needed.`
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      {/* Hero */}
      <section className="page-hero">
        <div style={{ fontSize: '80px', marginBottom: '24px' }}>{cuisine.emoji}</div>
        <h1>Best {cuisine.name} Restaurants Near You</h1>
        <p className="hero-tagline" style={{ fontSize: '20px', fontStyle: 'italic', opacity: 0.9, marginBottom: '16px' }}>
          {cuisine.tagline}
        </p>
        <p className="hero-subtitle">
          {cuisine.description}
        </p>
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '32px', flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-lg" onClick={() => handleNavigate('/try')}>
            Find {cuisine.name} Near Me
          </button>
          <button className="btn btn-secondary btn-lg" onClick={() => handleNavigate('/how-it-works')}>
            How It Works
          </button>
        </div>
      </section>

      {/* Must-Try Dishes */}
      {dishList.length > 0 && (
        <section className="content-section">
          <h2 className="section-title">Must-Try {cuisine.name} Dishes</h2>
          <p className="section-subtitle">The dishes that define {cuisine.name} cuisine</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginTop: '40px' }}>
            {dishList.map((item, i) => (
              <div key={i} className="mode-detail-card" style={{ padding: '24px' }}>
                <h3 style={{ fontSize: '18px', marginBottom: '8px', color: '#FF6347' }}>{item.dish}</h3>
                <p style={{ fontSize: '14px', margin: 0, lineHeight: 1.6 }}>{item.note}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* History / Background */}
      {cuisine.history && (
        <section className="content-section" style={{ textAlign: 'center' }}>
          <h2 className="section-title">The Story Behind {cuisine.name} Food</h2>
          <p style={{ fontSize: '17px', lineHeight: 1.8, maxWidth: '800px', margin: '0 auto' }}>
            {cuisine.history}
          </p>
        </section>
      )}

      {/* Pairs With + Price + Mood */}
      <section className="content-section">
        <h2 className="section-title">Good to Know</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginTop: '32px' }}>
          {cuisine.pairsWith && (
            <div className="mode-detail-card" style={{ padding: '24px', textAlign: 'center' }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>🍷</div>
              <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>Pairs Well With</h3>
              {cuisine.pairsWith.map((p, i) => (
                <p key={i} style={{ fontSize: '14px', margin: '4px 0', opacity: 0.85 }}>{p}</p>
              ))}
            </div>
          )}
          {cuisine.priceRange && (
            <div className="mode-detail-card" style={{ padding: '24px', textAlign: 'center' }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>💰</div>
              <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>Price Range</h3>
              <p style={{ fontSize: '18px', fontWeight: 600 }}>{cuisine.priceRange}</p>
              <p style={{ fontSize: '13px', opacity: 0.7, marginTop: '8px' }}>Filter by budget in Hangrily</p>
            </div>
          )}
          {cuisine.moodMatch && (
            <div className="mode-detail-card" style={{ padding: '24px', textAlign: 'center' }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>✨</div>
              <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>Best For</h3>
              <p style={{ fontSize: '14px', lineHeight: 1.6 }}>{cuisine.moodMatch}</p>
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="content-section">
        <h2 className="section-title">How to Find {cuisine.name} Restaurants with Hangrily</h2>

        <div className="process-flow">
          <div className="process-step">
            <div className="step-icon">📍</div>
            <div className="step-details">
              <span className="step-badge">Step 1</span>
              <h2>Share Your Location</h2>
              <p>Allow GPS access or search for a neighborhood. Hangrily pulls {cuisine.name} restaurants from Google Places near you.</p>
            </div>
          </div>

          <div className="process-step">
            <div className="step-icon">{cuisine.emoji}</div>
            <div className="step-details">
              <span className="step-badge">Step 2</span>
              <h2>Filter to {cuisine.name}</h2>
              <p>Set the cuisine filter to {cuisine.name}. Add price range and distance preferences to narrow it down further.</p>
            </div>
          </div>

          <div className="process-step">
            <div className="step-icon">👆</div>
            <div className="step-details">
              <span className="step-badge">Step 3</span>
              <h2>Swipe Through Options</h2>
              <p>See {cuisine.name} restaurants one at a time with photos, ratings, and distance. Right for yes, left for no. Solo or with your group.</p>
            </div>
          </div>

          <div className="process-step">
            <div className="step-icon">🎉</div>
            <div className="step-details">
              <span className="step-badge">Step 4</span>
              <h2>Eat {cuisine.name} Tonight</h2>
              <p>Your top-swiped {cuisine.name} spot wins. Get the address, check hours, and go enjoy{dishList[0] ? ` some ${dishList[0].dish}` : ''}.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Best Cities for This Cuisine */}
      {bestCities.length > 0 && (
        <section className="content-section">
          <h2 className="section-title">Best US Cities for {cuisine.name} Food</h2>
          <p className="section-subtitle">Where {cuisine.name} restaurants thrive</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginTop: '32px' }}>
            {bestCities.map(c => (
              <Link
                key={c.slug}
                to={`/city/${c.slug}`}
                className="mode-detail-card"
                style={{ padding: '24px', textAlign: 'center', cursor: 'pointer', textDecoration: 'none' }}
                onClick={() => window.scrollTo(0, 0)}
              >
                <h3 style={{ fontSize: '18px', marginBottom: '6px' }}>{c.name}, {c.state}</h3>
                <p style={{ fontSize: '14px', margin: 0, opacity: 0.7 }}>{c.restaurants} restaurants</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Other Cuisines */}
      <section className="content-section">
        <h2 className="section-title">Explore Other Cuisines</h2>
        <p className="section-subtitle">Not in the mood for {cuisine.name}? Try these</p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginTop: '40px' }}>
          {Object.entries(CUISINES).filter(([slug]) => slug !== cuisineSlug).slice(0, 8).map(([slug, data]) => (
            <Link
              key={slug}
              to={`/cuisine/${slug}`}
              className="mode-detail-card"
              style={{ padding: '24px', textAlign: 'center', cursor: 'pointer', textDecoration: 'none' }}
              onClick={() => window.scrollTo(0, 0)}
            >
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>{data.emoji}</div>
              <h3 style={{ fontSize: '20px', marginBottom: '8px' }}>{data.name}</h3>
              <p style={{ fontSize: '13px', margin: 0, opacity: 0.7 }}>{data.tagline}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="content-section alt-bg">
        <h2 className="section-title">Frequently Asked Questions</h2>
        <div className="faq-list">
          <details className="faq-item">
            <summary>How do I find the best {cuisine.name} restaurant near me?</summary>
            <p>Open Hangrily, set your cuisine filter to {cuisine.name}, and allow location access. The app pulls {cuisine.name} restaurants from Google Places near you, complete with photos and ratings. Swipe right on places that look good, left on ones that don't. Your top pick wins in under 2 minutes.</p>
          </details>
          <details className="faq-item">
            <summary>What are must-try {cuisine.name} dishes?</summary>
            <p>Essential {cuisine.name} dishes include {popularDishes}. {cuisine.history ? cuisine.history.slice(0, 150) : ''}</p>
          </details>
          <details className="faq-item">
            <summary>Can I pick a {cuisine.name} restaurant with my group using Hangrily?</summary>
            <p>Yes. Start a Group Session in Hangrily with the {cuisine.name} filter active. Up to 10 friends join via code and swipe through {cuisine.name} restaurants independently. The app finds the {cuisine.name} spot with the most group agreement — no arguing needed.</p>
          </details>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner">
        <div className="cta-content">
          <h2>Craving {cuisine.name}? Stop Scrolling, Start Swiping.</h2>
          <p>Find the perfect {cuisine.name} spot near you in under 2 minutes. Free to try.</p>
          <button className="btn" onClick={() => handleNavigate('/try')}>
            Find {cuisine.name} Near Me
          </button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

export default CuisinePage;
export { CUISINES };
